import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { eventActions } from '../../redux/slices/event.slice';
import { associationActions } from '../../redux/slices/association.slice';
import { organisationActions } from '../../redux/slices/organisation.slice';
import { teamActions } from '../../redux/slices/team.slice';

import EventCreateType from '../../components/event-create-type/event-create-type';
import EventCreateAssociation from '../../components/event-create-association/event-create-association';
import EventCreateOrganisation from '../../components/event-create-organisation/event-create-organisation';
import EventCreateTeam from '../../components/event-create-team/event-create-team';
import EventCreateForm from '../../components/event-create-form/event-create-form';

import { EEventCreateSteps } from '../../types/@event';
import { RootState } from '../../redux/store';

const EventsCreate = (): ReactElement => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const organisations = useSelector((state: RootState) => state.organisations.all);
  const teams = useSelector((state: RootState) => state.teams.all);
  const eventCreate = useSelector((state: RootState) => state.events.eventCreate);

  const organisationId = searchParams.get('organisationId') || '';
  const teamId = searchParams.get('teamId') || '';
  const requestedMode = searchParams.get('mode') === 'custom' ? 'custom' : 'quick';
  const organisationScoped = Boolean(organisationId);

  const initialStep = useMemo(() => {
    if (organisationScoped && requestedMode === 'quick') {
      return EEventCreateSteps.team;
    }

    return EEventCreateSteps.type;
  }, [organisationScoped, requestedMode]);

  const [step, setStep] = useState<number>(initialStep);

  useEffect(() => {
    dispatch(eventActions.resetEventCreate());
    dispatch(eventActions.setCreateMode(requestedMode));

    dispatch(associationActions.getAssociation(undefined));
    dispatch(organisationActions.getOrganisations(undefined));
    dispatch(teamActions.getTeams(undefined));
  }, [dispatch, requestedMode]);

  useEffect(() => {
    if (!organisationId || !organisations.length) {
      return;
    }

    const organisation = organisations.find((item) => String(item._id) === organisationId);
    if (organisation) {
      dispatch(eventActions.selectedOrganisation(organisation));
    }
  }, [dispatch, organisationId, organisations]);

  useEffect(() => {
    if (!teamId || !teams.length) {
      return;
    }

    const team = teams.find((item) => String(item._id) === teamId);
    if (team) {
      dispatch(eventActions.selectedTeam(team));
    }
  }, [dispatch, teamId, teams]);

  const switchToCustomFlow = (): void => {
    dispatch(eventActions.setCreateMode('custom'));
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('mode', 'custom');
      return next;
    });
    setStep(EEventCreateSteps.type);
  };

  const switchToQuickFlow = (): void => {
    dispatch(eventActions.setCreateMode('quick'));
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('mode');
      return next;
    });
    setStep(EEventCreateSteps.team);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {organisationScoped && (
        <Box
          sx={{
            maxWidth: '960px',
            margin: '0 auto',
            px: 3,
            pt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Box>
            <Box sx={{ fontSize: '22px', fontWeight: 700, color: '#17324d' }}>
              {eventCreate.createMode === 'quick' ? 'Create repeat event' : 'Create new style event'}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#6b7c8f', mt: 0.5 }}>
              Organisation is already known here. Most setups only need team and sponsor changes.
            </Box>
          </Box>

          {eventCreate.createMode === 'quick' ? (
            <Button variant="outlined" onClick={switchToCustomFlow}>
              Create new style event
            </Button>
          ) : (
            <Button variant="outlined" onClick={switchToQuickFlow}>
              Back to repeat event flow
            </Button>
          )}
        </Box>
      )}

      {step === EEventCreateSteps.type && (
        <EventCreateType
          setStep={setStep}
          organisationScoped={organisationScoped}
          onQuickRepeat={organisationScoped ? switchToQuickFlow : undefined}
        />
      )}

      {step === EEventCreateSteps.association && <EventCreateAssociation setStep={setStep} />}

      {step === EEventCreateSteps.organisation && <EventCreateOrganisation setStep={setStep} />}

      {step === EEventCreateSteps.team && (
        <EventCreateTeam
          setStep={setStep}
          organisationScoped={organisationScoped}
          onCreateNewStyle={organisationScoped ? switchToCustomFlow : undefined}
        />
      )}

      {step === EEventCreateSteps.event && (
        <EventCreateForm
          setStep={setStep}
          organisationScoped={organisationScoped}
          onCreateNewStyle={organisationScoped ? switchToCustomFlow : undefined}
        />
      )}
    </Box>
  );
};

export default EventsCreate;

